import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { KAL_DIALOG_DATA, KalDialogRef } from '@kalidea/kaligraphi';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    DocumentBankListComponent
} from 'src/app/_shared/dialogs/document-bank-dialog/document-bank-list/document-bank-list.component';
import {
    FichierCompletModel,
    GedCriteresFiltrageGedModelInput,
    GedListeDocumentsGedModel,
    MutationGetListeDocumentsGedModelArgs,
    MutationSaveDocumentGedArgs
} from 'src/graphql/generated-types';
import { map, switchMap, tap } from 'rxjs/operators';
import gql from 'graphql-tag';
import { merge, Observable } from 'rxjs';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    DocumentBankFilters
} from 'src/app/_shared/dialogs/document-bank-dialog/document-bank-filters/document-bank-filters.component';

export type PartialDocumentBankFilters = Partial<Record<keyof DocumentBankFilters, boolean>>;

export interface DocumentBankListDialogData {
    accepted?: boolean;
    selectedDocument?: FichierCompletModel;

    // utilisé dans le cas d'un upload de fichier local,
    // si true : l'appel graphql pour le sauvegarder sera ignoré (donc IdFile restera à 0)
    shouldDisableSaveDocument?: boolean;
    shouldToggleLocalFileUpload?: boolean;

    disabledFilters?: PartialDocumentBankFilters;
}

@Component({
    selector: 'app-document-bank-list-dialog',
    templateUrl: './document-bank-list-dialog.component.html',
    styleUrls: ['./document-bank-list-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DocumentBankListDialogComponent extends BaseComponent implements OnInit {

    form: FormGroup;

    selectedDocument: FichierCompletModel = null; // assigné dans deux cas : fichier local || document sélectionné dans la Ged

    shouldDisplayLocalFile = false;

    @ViewChild(DocumentBankListComponent) listComponent: DocumentBankListComponent;

    constructor(private readonly dialogRef: KalDialogRef<DocumentBankListDialogComponent>,
                private readonly graphqlService: GraphqlService,
                private readonly fb: FormBuilder,
                @Inject(KAL_DIALOG_DATA) readonly data: DocumentBankListDialogData,
                protected readonly cdr: ChangeDetectorRef) {
        super(cdr);
    }

    submit(): void {
        const dialogClosingData = {
            accepted: true,
            selectedDocument: this.selectedDocument,
        };

        if (this.form.value.localFile && !this.data.shouldDisableSaveDocument) {
            this.watch$ = this.wrapLoader$(this.saveLocalDocument$()).pipe(
                tap(() => this.dialogRef.close(dialogClosingData))
            );
        } else {
            this.dialogRef.close(dialogClosingData);
        }
    }

    close(): void {
        this.dialogRef.close({accepted: false});
    }

    toggleAddLocalFile(): void {
        this.shouldDisplayLocalFile = !this.shouldDisplayLocalFile;
    }

    get isOpenedFilters(): boolean {
        return !!this.listComponent?.isOpenedFilters;
    }

    private getListeDocumentsGedModel$(criteres: GedCriteresFiltrageGedModelInput): Observable<GedListeDocumentsGedModel> {
        return this.graphqlService.mutateOne<'GetListeDocumentsGEDModel', MutationGetListeDocumentsGedModelArgs>({
            mutation: gql`
            mutation getDocumentsList($contexte: ContextInfosInput, $criteres: GEDCriteresFiltrageGEDModelInput) {
                GetListeDocumentsGEDModel(contexte: $contexte, criteres: $criteres) {
                    ... documents
                }
            }
            fragment documents on GEDListeDocumentsGEDModel {
                NbElementTot
                ListeDocuments{
                    FichierCompletModel{
                        IdFile
                        Extension
                        Filename
                        Url
                        Token
                        DateCreation
                        StatutCode
                        StatutLibelle
                        Tags{
                            string
                        }
                    }
                }
            }`,
            variables: {criteres}
        });
    }

    private saveLocalDocument$(): Observable<GedListeDocumentsGedModel> {
        const {
            IdStatut,
            IdCategorie,
            ...documentToSave
        } = this.form.value.localFile;

        return this.graphqlService.mutateOne<'SaveDocumentGED', MutationSaveDocumentGedArgs>({
            mutation: gql`
                mutation SaveDocumentGED($contexte: ContextInfosInput, $doc: FichierCompletModelInput) {
                    SaveDocumentGED(contexte: $contexte, doc: $doc) {
                        NbElementTot
                    }
                }
            `,
            variables: {
                doc: documentToSave
            }
        }).pipe(
            // Attention: Le back renvois toujours les 25 premiers documents en retour à SaveDocumentGED
            // Récupère le total de fichiers retourné par l'ajout
            map((documents: GedListeDocumentsGedModel) => documents?.NbElementTot || 0),
            // Récupère les 20 derniers fichiers
            switchMap((nbElmtTotal: number) =>
                this.getListeDocumentsGedModel$({
                    OrderByID: true,
                    Skip: Math.max(0, nbElmtTotal - 20),
                    Take: 20
                })
            ),
            // Cherche le nom du fichier uploadé parmi les derniers ajoutés pour le sélectionner
            tap((documents: GedListeDocumentsGedModel) => {
                const generatedDocument = documents.ListeDocuments.FichierCompletModel
                    .sort((a: FichierCompletModel, b: FichierCompletModel) => a.IdFile === b.IdFile ? 0 : (a.IdFile > b.IdFile ? -1 : 1))
                    .find(e => e.Filename === this.selectedDocument.Filename.slice(0, -(this.selectedDocument.Extension.length + 1)));

                // On met à jour "selectedDocument" en remplaçant son id "0" par l'id généré par le back
                this.selectedDocument.IdFile = generatedDocument.IdFile;
                this.selectedDocument.Url = generatedDocument.Url;
                this.selectedDocument.Token = generatedDocument.Token;
                this.selectedDocument.Filename = generatedDocument.Filename;
                this.selectedDocument.Extension = generatedDocument.Extension;
            })
        );
    }

    private initSubscription(): void {
        const {
            localFile,
            fileFromList
        } = this.form.controls;

        this.watch$ = merge(
            this.form.valueChanges.pipe(
                tap(({localFile, fileFromList}) => {
                    const {
                        IdStatut,
                        IdCategorie,
                        ...realLocalFile
                    } = localFile || {};
                    this.selectedDocument = fileFromList || realLocalFile;
                })
            ),
            localFile.valueChanges.pipe(
                tap(() => fileFromList.reset(null, {emitEvent: false}))
            ),
            fileFromList.valueChanges.pipe(
                tap(() => localFile.reset(null, {emitEvent: false}))
            )
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            localFile: [null],
            fileFromList: [null]
        });

        this.initSubscription();

        if (this.data.shouldToggleLocalFileUpload) {
            this.toggleAddLocalFile();
        }
    }

}
